import { useMemo } from 'react';
import { Box, Typography, Avatar, Chip } from '@mui/material';
import {
  Group as GroupIcon,
  AttachFile as AttachFileIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import { formatTime, getInitials } from './chatUtils';

const ChatList = ({
  conversations,
  onSelectConversation,
  isExpanded,
  hideNavbar = false,
}) => {
  const handleSelectConversation = (conversation) => {
    onSelectConversation(conversation);
  };

  const styles = useMemo(() => ({
    container: {
      display: 'flex',
      height: '100%',
      width: '100%',
    },
    content: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    root: {
      flex: 1,
      overflowY: 'auto',
      height: '100%',
      maxHeight: 'calc(100%)', // Account for the filter container height
    },
    filterContainer: {
      p: 1.5,
      bgcolor: 'background.paper',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
    },
    filterChips: {
      display: 'flex',
      gap: 1,
      flexWrap: 'wrap',
    },
    conversationItem: {
      display: 'flex',
      alignItems: 'flex-start',
      m: isExpanded ? 1.2 : 0.75,
      p: 0.5,
      borderRadius: "12px",
      cursor: 'pointer',
      '&:hover': {
        bgcolor: 'action.hover',
        scale: isExpanded ? "1.005" : "1.01",
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
      },
      transition: 'background-color 0.25s ease, transform 0.2s ease, box-shadow 0.2s ease',
      transformOrigin: 'center',
    },
    avatar: {
      width: 36,
      height: 36,
      background: "#e5e7eb",
      color: "#212121",
      fontSize: "14px",
      fontWeight: "500",
      transition: 'transform 0.2s ease',
      mt: 0.4,
      '&:hover': {
        transform: 'scale(1.05)',
      },
    },
    groupAvatar: {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
    },
    messageContent: {
      ml: "12px",
      flex: 1,
      minWidth: 0,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      mb: 0.2,
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      gap: 0,
    },
    pinIcon: {
      color: 'primary.main',
      fontSize: '1rem',
    },
    timestamp: {
      fontSize: '0.75rem',
      color: 'text.secondary',
      whiteSpace: 'nowrap',
      ml: 1,
    },
    participants: {
      fontSize: '0.75rem',
      color: 'text.secondary',
      mb: 0.5,
    },
    lastMessage: {
      display: 'flex',
      alignItems: 'center',
      gap: 0,
    },
    messageText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontSize: "13px",
      color: "#4B5563",
      flexGrow: 1,
    },
    unreadBadge: {
      ml: 1,
      width: "22px",
      height: "20px",
      fontSize: "10px",
      display: "flex",
      alignSelf: "flex-end",
      transition: 'transform 0.2s ease',
      '&:hover': {
        transform: 'scale(1.1)',
      },
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      // py: 6,
      px: 2,
      textAlign: 'center',
      color: 'text.secondary',
    },
    emptyStateIcon: {
      fontSize: 48,
      color: 'action.disabled',
      mb: 2,
      animation: 'pulse 2s infinite ease-in-out',
      '@keyframes pulse': {
        '0%': { opacity: 0.6 },
        '50%': { opacity: 1 },
        '100%': { opacity: 0.6 },
      },
    },
  }), [isExpanded]);

  return (
    <Box sx={hideNavbar ? styles.content : styles.container}>
      <Box sx={hideNavbar ? { height: '100%' } : styles.content}>
        <Box sx={styles.root}>
          {conversations.length > 0 ? (
            conversations.map((conversation) => (
              <Box
                key={conversation.id}
                sx={styles.conversationItem}
                onClick={() => handleSelectConversation(conversation)}
              >
                {conversation.isGroup ? (
                  <Avatar sx={{ ...styles.avatar, ...styles.groupAvatar }}>
                    <GroupIcon />
                  </Avatar>
                ) : (
                  <Avatar sx={styles.avatar}>
                    {getInitials(conversation.title)}
                  </Avatar>
                )}

                <Box sx={styles.messageContent}>
                  <Box sx={conversation.lastMessage ? styles.header : {
                    display: "flex",
                    mt: 1,
                  }} >
                    <Box sx={conversation.lastMessage ? styles.title : {}}>
                      <Typography noWrap sx={{ fontSize: '15px', fontWeight: '500', color: '#212121' }}>
                        {conversation.title}
                      </Typography>
                    </Box>
                    {conversation.lastMessage && (
                      <Typography sx={styles.timestamp}>
                        {formatTime(conversation.lastMessage.timestamp)}
                      </Typography>
                    )}
                  </Box>

                  {conversation.isGroup && (
                    <Typography sx={styles.participants} noWrap>
                      {conversation.participants.map(p => p.name).join(', ')}
                    </Typography>
                  )}

                  {conversation.lastMessage && (
                    <Box sx={styles.lastMessage}>
                      {conversation.lastMessage.senderId === 'current-user' && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ mr: 0.5 }}
                        >
                          You:
                        </Typography>
                      )}
                      <Typography sx={styles.messageText}>
                        {conversation.lastMessage.mediaUrl ? (
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                            <AttachFileIcon sx={{
                              fontSize: '1rem',
                              mr: 0.5,
                              transition: 'transform 0.2s ease',
                              animation: conversation.unreadCount > 0 ? 'wiggle 1s ease-in-out' : 'none',
                              '@keyframes wiggle': {
                                '0%': { transform: 'rotate(0deg)' },
                                '25%': { transform: 'rotate(-10deg)' },
                                '50%': { transform: 'rotate(10deg)' },
                                '75%': { transform: 'rotate(-5deg)' },
                                '100%': { transform: 'rotate(0deg)' },
                              }
                            }} />
                            {conversation.lastMessage.content}
                          </Box>
                        ) : (
                          conversation.lastMessage.content
                        )}
                      </Typography>
                      {conversation.unreadCount > 0 && (
                        <Chip
                          label={conversation.unreadCount}
                          color="primary"
                          size="small"
                          sx={styles.unreadBadge}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            ))
          ) : (
            <Box sx={styles.emptyState}>
              <ForumIcon sx={styles.emptyStateIcon} />
              <Typography variant="h6" gutterBottom>
                No conversations found
              </Typography>
              <Typography variant="body2">
                Start a new conversation by searching for agents.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatList;